import { Children, isValidElement, type ReactNode } from 'react';
import CitationChip from './CitationChip';
import type { LegendaLang } from './vocabulary';

const TAG: Record<LegendaLang, string> = {
  ru: 'Синтез модели · не является источником',
  hy: 'Մոդելի սինթեզ · աղբյուր չէ',
  en: 'Model synthesis · not a source',
};

const DIGIT = /\d/;

/**
 * Walks a prose tree looking for a numeral that is not inside a citation.
 *
 * Recursion stops at `CitationChip`, which is the sanctioned carrier for a
 * figure. Everything else — plain strings, spans, emphasis — is inspected.
 */
function findBareNumeral(node: ReactNode): string | null {
  if (typeof node === 'string') {
    return DIGIT.test(node) ? node.trim() : null;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findBareNumeral(child);
      if (found) return found;
    }
    return null;
  }
  if (isValidElement(node)) {
    if (node.type === CitationChip) return null;
    const { children } = node.props as { children?: ReactNode };
    return children === undefined ? null : findBareNumeral(children);
  }
  return null;
}

export interface ModelProseProps {
  lang: LegendaLang;
  children: ReactNode;
}

/**
 * The model's voice: prose only, on a tinted ground, behind a marginal rule.
 *
 * Constraint 6 of the brief — "model prose is always distinguishable from
 * source-printed fact" — is a guideline a component drifts away from over time.
 * So the system states it as a rule the renderer checks: **a numeral may not
 * appear as plain text inside a model-voice block.** Every figure must be a
 * `CitationChip`, which carries its own verification rule and evidence link.
 *
 * In development the violation throws, so it surfaces the moment it is written.
 * In production it renders the prose without the offending block and reports —
 * losing a sentence is recoverable, presenting an unsourced number as archive
 * fact is not.
 */
export default function ModelProse({ lang, children }: ModelProseProps) {
  const bare = findBareNumeral(children);

  if (bare) {
    const message =
      `ModelProse: numeral "${bare}" appears outside a CitationChip. ` +
      'Every figure in model prose must carry its evidence — wrap it in <CitationChip>.';

    if (import.meta.env.DEV) {
      throw new Error(message);
    }

    console.error(message);
    return (
      <div className="lg-model">
        <span className="lg-model__tag">{TAG[lang]}</span>
        <p>—</p>
      </div>
    );
  }

  return (
    <div className="lg-model">
      <span className="lg-model__tag">{TAG[lang]}</span>
      {Children.count(children) === 1 && typeof children === 'string' ? (
        <p>{children}</p>
      ) : (
        children
      )}
    </div>
  );
}
